import { useEffect, useRef, useState } from "react";

/**
 * IntersectionObserver-based scroll reveal.
 * Returns [ref, isVisible]. Default triggers once at 15% visibility.
 */
export function useInView(options = {}) {
  const { threshold = 0.15, once = true } = options;
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            if (once) io.unobserve(e.target);
          } else if (!once) {
            setInView(false);
          }
        });
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, once]);
  return [ref, inView];
}

/**
 * Animated count-up that starts when the element comes into view.
 * @param {number} end
 * @param {object} opts { duration, decimals }
 */
export function useCountUp(end, { duration = 1800, decimals = 0 } = {}) {
  const [ref, inView] = useInView({ threshold: 0.4, once: true });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(eased * end);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setValue(end);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, end, duration]);

  const display = decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString("en-IN");
  return [ref, display, inView];
}
